import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import EditIcon from '@mui/icons-material/Edit';
import MenuIcon from '@mui/icons-material/Menu';
import SaveIcon from '@mui/icons-material/Save';
import { useContentContext } from '../../context/content-context';
import {
  DYNAMIC_VIEW_REDUCER_ACTIONS,
  useDynamicViewState,
  useDynamicViewDispatch,
} from '../../context/dynamic-view-context';
import { DpCardFilters } from './DpCardFilters';
import { ChartSelector } from '../layouts-management/ChartSelector';
import { getObjetivoDefaultStep } from '../../utils/default-step';

/**
 * @fileoverview Cabecera y acciones de configuración para tarjetas del Panel Dinámico.
 * @module DpCardTitleActions
 */

/**
 * Componente DpCardTitleActions.
 *
 * Gestiona el título, menú de opciones y configuración principal de cada tarjeta.
 *
 * Funcionalidades:
 * - Edición del título de la tarjeta.
 * - Eliminación de la tarjeta del layout.
 * - Cambio del tamaño de la tarjeta (columnas).
 * - Cambio del tipo de gráfico/componente mostrado.
 * - Menú contextual (popover) para configuraciones avanzadas.
 *
 * @returns {React.ReactElement} Cabecera completa de la tarjeta con controles.
 *
 * @author David Maldonado
 * (c) Servicios de Monitoreo S.A. - Santiago, Chile
 */
export const DpCardTitleActions = React.memo(function DpCardTitleActions() {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const { slot, handleChangeGridSize } = useContentContext();
  const state = useDynamicViewState();
  const dispatch = useDynamicViewDispatch();
  const [isEditingTitle, setIsEditingTitle] = React.useState(false);
  const [newTitle, setNewTitle] = React.useState(slot.title);
  const [isDrawerOpen, setIsDrawerOpen] = React.useState(false);

  function handleClick(event) {
    setAnchorEl(event.currentTarget);
  }

  function handleClickSaveTitle() {
    const updatedLayout = state.activeLayout.map((item) => {
      if (item.id === slot.id) {
        return { ...item, title: newTitle };
      }
      return item;
    });
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.UPDATE_TEMPLATE,
      payload: updatedLayout,
    });
    setIsEditingTitle(false);
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_IS_EDITING_TITLE,
      payload: false,
    });
  }

  function handleClickDeleteCard() {
    const confirm = window.confirm(t('dpCardTitleActions.deleteCardConfirm'));
    if (!confirm) {
      return;
    }
    const newLayout = state.activeLayout.filter((item) => item.id !== slot.id);
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.UPDATE_TEMPLATE,
      payload: newLayout,
    });
    setAnchorEl(null);
  }

  function handleClickEditTitle() {
    setIsEditingTitle(true);
    dispatch({
      type: DYNAMIC_VIEW_REDUCER_ACTIONS.SET_IS_EDITING_TITLE,
      payload: true,
    });
  }

  const onSubmitChangeChart = React.useCallback(
    ({ selectedComponentKey, newComponentTitle }) => {
      const pasoId = getObjetivoDefaultStep({
        objetivoId: state.globalObjective.objetivoId,
      });
      const updatedLayout = state.activeLayout.map((item) => {
        if (item.id === slot.id) {
          return {
            ...item,
            component: selectedComponentKey,
            title: newComponentTitle,
            customProperties: { pasoId },
          };
        }
        return item;
      });
      dispatch({
        type: DYNAMIC_VIEW_REDUCER_ACTIONS.UPDATE_TEMPLATE,
        payload: updatedLayout,
      });
      setAnchorEl(null);
    },
  );

  return (
    <React.Fragment>
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          padding={2}
          width="100%"
        >
          <Box width="85%">
            {state.isEditing ? (
              <>
                {isEditingTitle ? (
                  <Box display="flex" alignItems="center">
                    <TextField
                      variant="standard"
                      value={newTitle}
                      onChange={(e) => setNewTitle(e.target.value)}
                      inputProps={{
                        style: {
                          fontSize: 20,
                          fontWeight: '500',
                          minWidth: 250,
                          maxWidth: '100%',
                          fieldSizing: 'content',
                          color: '#8a8a8a',
                        },
                      }}
                    />
                    <IconButton
                      size="medium"
                      onClick={handleClickSaveTitle}
                      style={{ marginLeft: 10 }}
                    >
                      <SaveIcon />
                    </IconButton>
                  </Box>
                ) : (
                  <Box display="flex" alignItems="center" width="100%">
                    <Typography
                      style={{
                        textOverflow: 'ellipsis',
                        overflow: 'hidden',
                        whiteSpace: 'nowrap',
                        color: '#111827',
                        fontWeight: 700,
                        fontSize: '1.25rem',
                      }}
                    >
                      {slot.title}
                    </Typography>
                    <Box>
                      {state.isEditing && (
                        <IconButton
                          size="small"
                          onClick={handleClickEditTitle}
                          style={{ marginLeft: 10 }}
                          disabled={state.isEditingTitle}
                        >
                          <EditIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Box>
                )}
              </>
            ) : (
              <Box display="flex" alignItems="center">
                <Typography style={{ color: '#111827', fontWeight: 700, fontSize: '1.25rem' }}>
                  {slot.title}
                </Typography>
              </Box>
            )}
          </Box>
          <Box width="15%" display="flex" justifyContent="flex-end">
            {state.isEditing && (
              <IconButton onClick={handleClick} size="small">
                <MenuIcon />
              </IconButton>
            )}
          </Box>
        </Box>
        <Divider style={{ backgroundColor: '#E5E7EB' }} />
        <Box padding={2}>
          <DpCardFilters />
        </Box>
      </Box>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <Card style={{ width: 300 }}>
          <CardHeader title={t('dpCardTitleActions.panelSettings')} />
          <CardContent>
            <FormControl variant="outlined" fullWidth size="small">
              <InputLabel id="paneles-label">
                {t('dpCardTitleActions.cardSize')}
              </InputLabel>
              <Select
                defaultValue={slot.cols}
                labelId="paneles-label"
                label={t('dpCardTitleActions.cardSize')}
                onChange={(event) =>
                  handleChangeGridSize({
                    id: slot.id,
                    size: event.target.value,
                  })
                }
              >
                <MenuItem value={4}>{t('dpCardTitleActions.sizeS')}</MenuItem>
                <MenuItem value={6}>{t('dpCardTitleActions.sizeM')}</MenuItem>
                <MenuItem value={8}>{t('dpCardTitleActions.sizeG')}</MenuItem>
                <MenuItem value={12}>{t('dpCardTitleActions.sizeXL')}</MenuItem>
              </Select>
            </FormControl>
            <Box marginTop={2}>
              <ChartSelector
                onSubmit={onSubmitChangeChart}
                isOpen={isDrawerOpen}
                onClose={() => setIsDrawerOpen(false)}
              >
                <Button
                  fullWidth
                  color="primary"
                  variant="outlined"
                  onClick={() => setIsDrawerOpen(true)}
                >
                  {t('dpCardTitleActions.selectChart')}
                </Button>
              </ChartSelector>
            </Box>
            <Box marginTop={2}>
              <Button
                variant="outlined"
                style={{
                  borderColor: 'red',
                  backgroundColor: '#fc8397',
                  color: 'white',
                }}
                fullWidth
                onClick={handleClickDeleteCard}
              >
                {t('dpCardTitleActions.deleteCard')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Popover>
    </React.Fragment>
  );
});
